/**
 * Overview tab of the school management screen (ADM-8): dashboard metrics,
 * recent analyses, inactive students, the credit pool and quick actions.
 */
import React from 'react';
import { ActivityIndicator, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Text } from '@/components/ui/text';
import { SchoolCreditDashboard } from '../../../components/school/SchoolCreditDashboard';
import { useTranslation } from '../../../hooks/useTranslation';
import { canManageMentors, type User } from '../../../utils/roleUtils';
import { COLORS, type DashboardAnalytics, type School, type SchoolManagementTab } from '../schoolManagementTypes';

function MetricTile({
  icon,
  iconColor,
  value,
  label,
  subtext,
}: {
  icon: keyof typeof MaterialIcons.glyphMap;
  iconColor: string;
  value: string | number;
  label: string;
  subtext?: string;
}) {
  return (
    <Card className="flex-1 min-w-[150px]">
      <CardContent className="p-4">
        <View className="flex-row items-center mb-3">
          <View
            className="w-8 h-8 rounded-lg items-center justify-center"
            style={{ backgroundColor: `${iconColor}1A` }}
          >
            <MaterialIcons name={icon} size={18} color={iconColor} />
          </View>
        </View>
        <Text className="text-3xl font-bold text-foreground mb-1">{value}</Text>
        <Text className="text-sm font-medium text-foreground">{label}</Text>
        {subtext ? (
          <Text className="text-xs text-muted-foreground mt-0.5">{subtext}</Text>
        ) : null}
      </CardContent>
    </Card>
  );
}

function SectionLabel({ text }: { text: string }) {
  return (
    <Text className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3 mt-2">
      {text}
    </Text>
  );
}

export function OverviewTab({
  analytics,
  school,
  user,
  onInviteStudent,
  onSelectTab,
}: {
  analytics: DashboardAnalytics | null;
  school: School | null;
  user?: User;
  onInviteStudent: () => void;
  onSelectTab: (tab: SchoolManagementTab) => void;
}) {
  const { t } = useTranslation();

  if (!analytics) {
    return (
      <View className="mb-6">
        <Text className="text-xl font-bold text-foreground mb-4">
          {t('schoolManagement.schoolOverview')}
        </Text>
        <Card>
          <CardContent className="p-8 items-center">
            <ActivityIndicator size="large" color={COLORS.primary} />
            <Text className="text-base text-muted-foreground mt-4">
              {t('schoolManagement.loadingAnalytics')}
            </Text>
          </CardContent>
        </Card>
      </View>
    );
  }

  const activePct =
    analytics.total_students > 0
      ? Math.round((analytics.active_students_week / analytics.total_students) * 100)
      : 0;

  return (
    <View className="mb-6">
      <View className="mb-5">
        <Text className="text-2xl font-bold text-foreground">
          {t('schoolManagement.dashboardAnalytics')}
        </Text>
        {school?.name ? (
          <Text className="text-sm text-muted-foreground mt-1">{school.name}</Text>
        ) : null}
      </View>

      {/* ── Students at a glance ─────────────────────────────────────── */}
      <SectionLabel text={t('schoolManagement.sections.students')} />
      <View className="flex-row flex-wrap gap-3 mb-5">
        <MetricTile
          icon="people"
          iconColor={COLORS.success}
          value={`${analytics.active_students_week}/${analytics.total_students}`}
          label={t('schoolManagement.metrics.active7Days')}
          subtext={t('schoolManagement.metrics.activePercent', {
            pct: activePct,
            defaultValue: `${activePct}% active this week`,
          })}
        />
        <MetricTile
          icon="star"
          iconColor="#F59E0B"
          value={analytics.avg_icf_score ? analytics.avg_icf_score.toFixed(1) : '—'}
          label={t('schoolManagement.metrics.avgIcfScore')}
          subtext={t('schoolManagement.metrics.avgIcfSubtext', {
            defaultValue: 'Across all completed analyses',
          })}
        />
      </View>

      {/* ── This month's activity ─────────────────────────────────────── */}
      <SectionLabel text={t('schoolManagement.sections.thisMonth')} />
      <View className="flex-row flex-wrap gap-3 mb-5">
        <MetricTile
          icon="description"
          iconColor={COLORS.primary}
          value={analytics.transcripts_this_month}
          label={t('schoolManagement.metrics.transcriptsThisMonth')}
        />
        <MetricTile
          icon="chat"
          iconColor={COLORS.primary}
          value={analytics.chat_interactions_this_month}
          label={t('schoolManagement.metrics.chatsThisMonth')}
        />
      </View>

      {/* ── All-time totals ────────────────────────────────────────────── */}
      <SectionLabel text={t('schoolManagement.sections.allTime')} />
      <View className="flex-row flex-wrap gap-3 mb-6">
        <MetricTile
          icon="library-books"
          iconColor={COLORS.secondary}
          value={analytics.transcripts_total}
          label={t('schoolManagement.metrics.transcriptsTotal')}
        />
        <MetricTile
          icon="forum"
          iconColor={COLORS.secondary}
          value={analytics.chat_interactions_total}
          label={t('schoolManagement.metrics.chatsTotal')}
        />
        <MetricTile
          icon="upload-file"
          iconColor={COLORS.success}
          value={analytics.documents_uploaded}
          label={t('schoolManagement.metrics.documentsUploaded')}
        />
      </View>

      {/* Recent Activity Section */}
      {analytics.recent_analyses.length > 0 && (
        <Card className="mb-6">
          <CardHeader>
            <Text className="text-lg font-bold text-foreground">
              {t('schoolManagement.recentAnalysesTitle')}
            </Text>
          </CardHeader>
          <CardContent className="p-4">
            {analytics.recent_analyses.map((analysis, index) => (
              <View
                key={index}
                className={`flex-row items-center justify-between py-3 ${index < analytics.recent_analyses.length - 1 ? 'border-b border-gray-200' : ''
                  }`}
              >
                <View className="flex-1">
                  <Text className="text-sm font-semibold text-foreground">
                    {analysis.student_name}
                  </Text>
                  <Text className="text-xs text-muted-foreground">
                    {new Date(analysis.analyzed_at).toLocaleDateString()} •{' '}
                    {new Date(analysis.analyzed_at).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </Text>
                </View>
                <View
                  className={`px-3 py-1 rounded-full ${analysis.overall_score >= 70
                      ? 'bg-green-100'
                      : analysis.overall_score >= 50
                        ? 'bg-yellow-100'
                        : 'bg-red-100'
                    }`}
                >
                  <Text
                    className={`text-sm font-bold ${analysis.overall_score >= 70
                        ? 'text-green-700'
                        : analysis.overall_score >= 50
                          ? 'text-yellow-700'
                          : 'text-red-700'
                      }`}
                  >
                    {analysis.overall_score}/100
                  </Text>
                </View>
              </View>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Inactive Students Alert */}
      {analytics.inactive_students.length > 0 && (
        <Card className="mb-6 bg-yellow-50">
          <CardHeader>
            <View className="flex-row items-center">
              <MaterialIcons name="warning" size={20} color="#F59E0B" />
              <Text className="text-lg font-bold text-foreground ml-2">
                {t('schoolManagement.inactiveStudentsTitle', {
                  count: analytics.inactive_students.length,
                })}
              </Text>
            </View>
          </CardHeader>
          <CardContent className="p-4">
            <Text className="text-sm text-muted-foreground mb-3">
              {t('schoolManagement.inactiveStudentsDescription')}
            </Text>
            {analytics.inactive_students.slice(0, 5).map((student, index) => (
              <View
                key={student.student_id}
                className={`flex-row items-center py-2 ${index < Math.min(analytics.inactive_students.length, 5) - 1
                    ? 'border-b border-gray-200'
                    : ''
                  }`}
              >
                <MaterialIcons name="person-outline" size={18} color="#6b7280" />
                <View className="ml-2 flex-1">
                  <Text className="text-sm font-medium text-foreground">
                    {student.full_name}
                  </Text>
                  <Text className="text-xs text-muted-foreground">{student.email}</Text>
                </View>
              </View>
            ))}
            {analytics.inactive_students.length > 5 && (
              <Text className="text-xs text-muted-foreground mt-2 text-center">
                {t('schoolManagement.inactiveStudentsMore', {
                  count: analytics.inactive_students.length - 5,
                })}
              </Text>
            )}
          </CardContent>
        </Card>
      )}

      {/* Credit Pool Dashboard - Compact view for overview */}
      {school && <SchoolCreditDashboard schoolId={school.id} compact />}

      {/* Quick Actions */}
      {canManageMentors(user) && (
        <Card>
          <CardHeader>
            <Text className="text-lg font-bold text-foreground">
              {t('schoolManagement.quickActionsHeading')}
            </Text>
          </CardHeader>
          <CardContent className="p-4">
            <View className="flex-row flex-wrap gap-3">
              <Button
                variant="default"
                onPress={onInviteStudent}
                className="flex-1 min-w-[140px]"
              >
                <MaterialIcons name="person-add" size={18} color="white" />
                <Text className="text-white ml-2">
                  {t('schoolManagement.quickActionsButtons.inviteStudent')}
                </Text>
              </Button>
              <Button
                variant="secondary"
                onPress={() => onSelectTab('documents')}
                className="flex-1 min-w-[140px]"
              >
                <MaterialIcons name="upload-file" size={18} color="white" />
                <Text className="text-white ml-2">
                  {t('schoolManagement.quickActionsButtons.uploadDocument')}
                </Text>
              </Button>
              <Button
                variant="outline"
                onPress={() => onSelectTab('students')}
                className="flex-1 min-w-[140px]"
              >
                <MaterialIcons name="list" size={18} color="#1F2937" />
                <Text className="text-foreground ml-2">
                  {t('schoolManagement.quickActionsButtons.viewStudents')}
                </Text>
              </Button>
            </View>
          </CardContent>
        </Card>
      )}
    </View>
  );
}
